import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SocratesLogo } from '@/components/SocratesLogo';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, Lock } from 'lucide-react';

export default function Register() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Invite-only: if no invite code, show restricted message
  if (!inviteCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-stone-100 p-3 rounded-full">
              <Lock className="w-8 h-8 text-stone-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Invite Only</h2>
          <p className="text-stone-600 mb-6">
            Socrates is an invite-only league. You need an invitation link to create an account.
          </p>
          <p className="text-sm text-stone-500 mb-6">
            Already have an account?
          </p>
          <Link to="/login">
            <Button className="w-full">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match.',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    
    // If user has an invite code, redirect them back to the invite page after email confirmation
    // This ensures they can complete the invite acceptance flow
    const redirectTo = inviteCode 
      ? `${window.location.origin}/invite/${inviteCode}`
      : undefined;
    
    const { error, session } = await signUp(email, password, name, redirectTo);

    if (error) {
      toast({
        title: 'Registration failed',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    // If we got a session back, user was auto-confirmed (no email verification needed)
    // Redirect them directly to the invite page or dashboard
    if (session) {
      toast({
        title: 'Account created!',
        description: 'Welcome to Socrates.',
      });
      navigate(inviteCode ? `/invite/${inviteCode}` : '/dashboard');
      return;
    }

    // Otherwise, email confirmation is required — show "check your email" screen
    setSuccess(true);
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Check your email</h2>
          <p className="text-stone-600 mb-6">
            We sent a confirmation link to <strong>{email}</strong>. Please click the link to verify your account.
          </p>
          {inviteCode && (
            <p className="text-sm text-stone-500 mb-4">
              After confirming your email, you'll be redirected to accept your league invitation.
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="w-full"
          >
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <SocratesLogo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 font-caveat">Socrates</h1>
          <p className="text-stone-600">Create your account</p>
          {inviteCode && (
            <p className="text-sm text-green-600 mt-2">
              You've been invited to join a league!
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={!!searchParams.get('email')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={6}
            />
            <p className="text-xs text-stone-500">Must be at least 6 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-stone-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-stone-800 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

